import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import { Icon } from '@iconify/react';

import Iconify from 'src/components/iconify';
import { shortAddress } from 'src/utils/shortAddress';

DisconnectConfirmDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  address: PropTypes.string,
  isDisconnecting: PropTypes.bool,
};

export default function DisconnectConfirmDialog({
  open,
  onClose,
  onConfirm,
  address,
  isDisconnecting,
}) {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableEnforceFocus
      disableRestoreFocus
      disableScrollLock
      fullWidth
      maxWidth="xs"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: alpha(theme.palette.common.black, 0.52),
            backdropFilter: 'blur(4px)',
          },
        },
        paper: {
          sx: {
            width: 'clamp(320px, 92vw, 420px)',
            boxSizing: 'border-box',
            maxWidth: 420,
            maxHeight: '90vh',
            overflow: 'hidden',
            borderRadius: 4,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow:
              theme.palette.mode === 'light'
                ? '0 24px 64px rgba(16, 24, 40, 0.18)'
                : '0 24px 64px rgba(0, 0, 0, 0.45)',
            backgroundColor: theme.palette.background.paper,
            // Warm amber tint in header gradient to signal a destructive action —
            // same gradient technique as LoginModal but hued for "warning" intent.
            backgroundImage: `linear-gradient(180deg, ${alpha(
              theme.palette.warning.main,
              0.07
            )} 0%, transparent 28%)`,
          },
        },
      }}
    >
      {/* ── Header (mirrors LoginModal header block) ── */}
      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          pt: { xs: 2, sm: 2.25 },
          pb: 1.5,
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          {/* Wallet icon badge */}
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2.5,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.28)}`,
                bgcolor: alpha(theme.palette.warning.main, 0.1),
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <Icon icon="solar:wallet-bold" width={20} color={theme.palette.warning.main} />
            </Box>

            <Box sx={{ pr: 1 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  letterSpacing: '-0.03em',
                  lineHeight: 1.1,
                  mt: 0.25,
                }}
              >
                Disconnect wallet?
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.75, lineHeight: 1.65 }}
              >
                {address
                  ? `You're about to disconnect ${shortAddress(address)}.`
                  : `You're about to disconnect your wallet.`}
              </Typography>
            </Box>
          </Stack>

          {/* Close button — same style as LoginModal */}
          <IconButton
            onClick={onClose}
            aria-label="Close disconnect dialog"
            disabled={isDisconnecting}
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: alpha(theme.palette.background.default, 0.72),
              flexShrink: 0,
            }}
          >
            <Iconify icon="mingcute:close-line" width={18} />
          </IconButton>
        </Stack>
      </Box>

      <Divider />

      {/* ── Body (mirrors DialogContent block) ── */}
      <DialogContent
        sx={{
          px: { xs: 2, sm: 2.5 },
          pt: 2,
          pb: { xs: 2.5, sm: 3 },
          overflowY: 'auto',
        }}
      >
        {/* Info card */}
        <Box
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
            bgcolor: alpha(theme.palette.warning.main, 0.06),
            px: 2,
            py: 1.5,
            mb: 2.5,
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <Box sx={{ mt: 0.1, color: theme.palette.warning.main, flexShrink: 0 }}>
              <Iconify icon="solar:info-circle-bold" width={18} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
              You will need to reconnect your wallet to perform swaps or sign transactions.
            </Typography>
          </Stack>
        </Box>

        {/* CTA row */}
        <Stack spacing={1.25}>
          {/* Primary — destructive disconnect */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={onConfirm}
            disabled={isDisconnecting}
            startIcon={isDisconnecting ? null : <Iconify icon="solar:logout-3-bold" width={18} />}
            sx={{
              height: 48,
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: 800,
              fontSize: '0.9rem',
              letterSpacing: '-0.01em',
              boxShadow: 'none',
              bgcolor: theme.palette.error.main,
              color: '#fff',
              '&:hover': {
                bgcolor: theme.palette.error.dark,
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                bgcolor: alpha(theme.palette.error.main, 0.5),
                color: alpha('#fff', 0.7),
              },
            }}
          >
            {isDisconnecting ? 'Disconnecting…' : 'Yes, disconnect'}
          </Button>

          {/* Secondary — cancel */}
          <Button
            fullWidth
            variant="outlined"
            size="large"
            onClick={onClose}
            disabled={isDisconnecting}
            sx={{
              height: 48,
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.9rem',
              letterSpacing: '-0.01em',
              boxShadow: 'none',
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
              bgcolor: alpha(theme.palette.background.default, 0.6),
              '&:hover': {
                borderColor: alpha(theme.palette.text.primary, 0.3),
                bgcolor: alpha(theme.palette.action.hover, 0.6),
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                borderColor: theme.palette.divider,
                color: alpha(theme.palette.text.primary, 0.4),
              },
            }}
          >
            Cancel
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
